import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, formatNumber, titleCase } from '../../lib/format'
import type { Load, Truck } from '../../types'
import {
  Alert, Card, CardBody, CardHeader, EmptyState, Skeleton, StatusBadge,
} from '../../components/ui'
import { TruckIcon } from '../../components/icons'

/* Individual-resource grants - the read-only view of exactly the trucks,
 * hired equipment and loads a super admin has picked out for this user (see
 * role_service.granted_truck_ids() and UserEdit.tsx's ResourcePicker).
 *
 * Deliberately its own page rather than folding these into the existing
 * /trucks, /equipment and /loads pages: those are org-scoped operational
 * screens gated behind leader/ops capabilities, whereas a grant recipient
 * may hold no operational capability and no org at all. Keeping it separate
 * means the grant can never widen what those screens show, and this page
 * needs no capability of its own - the data itself is the permission. */

interface HiredEquipment {
  id?: string
  name?: string
  equipment_type?: string
  status?: string
  supplier_name?: string
  daily_rate?: number
  currency?: string
}

export default function SharedWithMe() {
  const trucks = useQuery({
    queryKey: ['ops', 'my-trucks'],
    queryFn: () => api.get<{ ok: boolean; items: Truck[] }>('/ops/my-trucks'),
  })
  const equipment = useQuery({
    queryKey: ['ops', 'my-equipment'],
    queryFn: () => api.get<{ ok: boolean; items: HiredEquipment[] }>('/ops/my-equipment'),
  })
  const loads = useQuery({
    queryKey: ['ops', 'my-loads'],
    queryFn: () => api.get<{ ok: boolean; items: Load[] }>('/ops/my-loads'),
  })

  const truckItems = trucks.data?.items ?? []
  const equipmentItems = equipment.data?.items ?? []
  const loadItems = loads.data?.items ?? []
  const anyLoading = trucks.isLoading || equipment.isLoading || loads.isLoading
  const total = truckItems.length + equipmentItems.length + loadItems.length
  const firstError = trucks.error ?? equipment.error ?? loads.error

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Shared with me</h1>
          <p className="page-head__sub">
            Trucks, equipment and loads an administrator has given you sight of.
          </p>
        </div>
      </div>

      {firstError && <Alert tone="danger">{(firstError as Error).message}</Alert>}

      {anyLoading ? (
        <div className="stack"><Skeleton h={90} /><Skeleton h={140} /></div>
      ) : total === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<TruckIcon size={22} />}
              title="Nothing shared with you yet"
              body="When an administrator gives you access to specific trucks, equipment or loads, they'll appear here."
            />
          </CardBody>
        </Card>
      ) : (
        <div className="stack" style={{ gap: 'var(--sp-6)' }}>
          {truckItems.length > 0 && (
            <Card>
              <CardHeader title="Trucks" sub={`${truckItems.length} shared with you`} />
              <CardBody flush>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Plate</th><th>Name</th><th>Kind</th><th>Status</th></tr></thead>
                    <tbody>
                      {truckItems.map((t) => (
                        <tr key={t.id}>
                          <td style={{ fontWeight: 600 }}>{t.plate || '—'}</td>
                          <td>{t.name || '—'}</td>
                          <td>{titleCase(t.kind) || '—'}</td>
                          <td><StatusBadge status={t.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}

          {equipmentItems.length > 0 && (
            <Card>
              <CardHeader title="Hired equipment" sub={`${equipmentItems.length} shared with you`} />
              <CardBody flush>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Name</th><th>Type</th><th>Supplier</th><th>Status</th></tr></thead>
                    <tbody>
                      {equipmentItems.map((e) => (
                        <tr key={e.id}>
                          <td style={{ fontWeight: 600 }}>{e.name || '—'}</td>
                          <td>{titleCase(e.equipment_type) || '—'}</td>
                          <td>{e.supplier_name || '—'}</td>
                          <td><StatusBadge status={e.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}

          {loadItems.length > 0 && (
            <Card>
              <CardHeader title="Loads" sub={`${loadItems.length} shared with you`} />
              <CardBody flush>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>Date</th><th>Mineral</th><th className="table__num">Tonnage</th><th>Route</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {loadItems.map((l) => (
                        <tr key={l.id}>
                          <td>{formatDate(l.load_time || l.created_at)}</td>
                          <td style={{ fontWeight: 600 }}>{titleCase(l.mineral_type) || 'Load'}</td>
                          <td className="table__num">{formatNumber(l.tonnage, 1)} t</td>
                          <td>{[l.source_label, l.destination_label].filter(Boolean).join(' → ') || '—'}</td>
                          <td><StatusBadge status={l.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          )}
        </div>
      )}
    </>
  )
}
